/** Employee count bands — aligned with onboarding / manager-approved list. */
export const RECOMMENDATION_EMPLOYEE_COUNT_OPTIONS = [
  { value: '1-500', label: '1 – 500' },
  { value: '1,000-5,000', label: '1,000 – 5,000' },
  { value: '5,000-10,000', label: '5,000 – 10,000' },
  { value: '10,000-30,000', label: '10,000 – 30,000' },
  { value: '30,000-50,000', label: '30,000 – 50,000' },
  { value: '50,000+', label: '50,000+' },
] as const;

/** Two-column geographic region layout (manager-approved list). */
export const RECOMMENDATION_REGION_COLUMNS: readonly [readonly string[], readonly string[]] = [
  ['North America', 'South America', 'Middle East', 'Asia'],
  [
    'Central America and the Caribbean',
    'Europe',
    'Africa',
    'Australia, New Zealand, and Pacific Islands',
  ],
] as const;

export const RECOMMENDATION_REGIONS = [
  ...RECOMMENDATION_REGION_COLUMNS[0],
  ...RECOMMENDATION_REGION_COLUMNS[1],
] as const;

/** Approved 23-topic list (training matrix catalog). */
export const RECOMMENDATION_TRAINING_TOPICS = [
  'AI Responsible Use',
  'Anti-Harassment and Bullying',
  'Bribery and Corruption',
  'Conflicts of Interest',
  'Cybersecurity',
  'Welcome to the Code of Conduct Course',
  'Fair Competition',
  'Fraud',
  'GDPR',
  'Gifts and Entertainment',
  'HIPAA',
  'Import Export Controls',
  'Insider Trading',
  'Interaction with Healthcare Professionals',
  'Manager Responsibility',
  'Modern Slavery Prevention',
  'Privacy',
  'Respect in the Workplace',
  'Speak Up and Non-Retaliation',
  'Third Party Expectations',
  'Trade Compliance',
  'Trade Sanctions',
  'Anti-Money Laundering',
] as const;

/** Two-column layout for priority topic checkboxes (CEE-499 Step 4). */
const trainingTopicMidpoint = Math.ceil(RECOMMENDATION_TRAINING_TOPICS.length / 2);
export const RECOMMENDATION_TRAINING_TOPIC_COLUMNS: readonly [readonly string[], readonly string[]] = [
  RECOMMENDATION_TRAINING_TOPICS.slice(0, trainingTopicMidpoint),
  RECOMMENDATION_TRAINING_TOPICS.slice(trainingTopicMidpoint),
] as const;

export const RECOMMENDATION_MAX_MONTH_CONSTRAINTS = 6;

/** Calendar months for schedule constraints (must complete / cannot schedule). */
export const RECOMMENDATION_MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const RECOMMENDATION_TRAINING_PACKAGES = [
  {
    value: 'essentials',
    labelKey: 'essentials',
    descriptionKey: 'essentials_description',
  },
  {
    value: 'pro',
    labelKey: 'pro',
    descriptionKey: 'pro_description',
  },
  {
    value: 'plus',
    labelKey: 'plus',
    descriptionKey: 'plus_description',
  },
] as const;

export type RecommendationTrainingPackage =
  (typeof RECOMMENDATION_TRAINING_PACKAGES)[number]['value'];

/** Max client priority topics per training package (aligned with onboarding wizard). */
export const PACKAGE_MAX_TRAINING_TOPICS: Record<RecommendationTrainingPackage, number> = {
  essentials: 5,
  pro: 8,
  plus: 10,
};

/** Upper bound for Plus — use {@link getMaxTrainingTopicsForPackage} per package. */
export const RECOMMENDATION_MAX_TRAINING_TOPICS = PACKAGE_MAX_TRAINING_TOPICS.plus;

export function getMaxTrainingTopicsForPackage(
  trainingPackage: RecommendationTrainingPackage | '' | null | undefined,
): number {
  if (
    trainingPackage === 'essentials' ||
    trainingPackage === 'pro' ||
    trainingPackage === 'plus'
  ) {
    return PACKAGE_MAX_TRAINING_TOPICS[trainingPackage];
  }
  return PACKAGE_MAX_TRAINING_TOPICS.plus;
}

export function trimPriorityTopicsToPackageLimit(
  topics: string[],
  trainingPackage: RecommendationTrainingPackage | '' | null | undefined,
): string[] {
  return topics.slice(0, getMaxTrainingTopicsForPackage(trainingPackage));
}
